import { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import hash from 'object-hash';

interface CacheRecord {
  createdAt: number;
  data: unknown;
}

export function createCacheInterceptors(axios: AxiosInstance): {
  cacheRequestInterceptor: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig;
  cacheResponseInterceptorOnFulfilled: (config: AxiosResponse) => AxiosResponse;
} {
  const cache: Record<string, CacheRecord | undefined> = {};

  return {
    cacheRequestInterceptor: (config) => {
      if (!config.useCache) {
        return config;
      }

      const cacheKey = hash([config.method, config.url, config.params, config.data]);
      const cachedValue = cache[cacheKey];

      if (cachedValue != null) {
        config = {
          ...config,
          metadata: { ...config.metadata, cached: true },
          adapter: (config) =>
            Promise.resolve({
              data: cachedValue.data,
              status: 200,
              statusText: 'OK',
              headers: config.headers,
              config,
            }),
        };
      }

      return config;
    },
    cacheResponseInterceptorOnFulfilled: (response) => {
      const config = response.config;
      if (!config.useCache) {
        return response;
      }

      const cacheKey = hash([config.method, config.url, config.params, config.data]);

      if (!config.metadata?.cached) {
        cache[cacheKey] = { createdAt: Date.now(), data: response.data };
      } else {
        const cachedValue = cache[cacheKey];
        if (cachedValue != null && Date.now() - cachedValue.createdAt > 60 * 1e3) {
          axios
            .request({ ...config, useCache: false })
            .then((response) => {
              cache[cacheKey] = { createdAt: Date.now(), data: response.data };
            })
            .catch((err) => {
              console.warn('unable to fetch data in background', err);
            });
        }
      }

      return response;
    },
  };
}
